import { useContext, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  alpha,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from '../../../lib/axios';
import { useSnackbar } from '../../../components/SnackbarProvider';
import type { Inventory } from '../../../types';
import { ColorModeContext } from '../../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../../theme/colors';

interface InventoryWithLocation extends Inventory {
  locationName?: string;
}

interface QuickStockAdjustmentDialogProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  preselectedLocationId?: string;
}

interface StockAdjustmentFormData {
  locationId: string;
  quantityChange: number;
  reason: 'restock' | 'loss' | 'count' | 'adjustment';
  notes?: string;
}

const fetchProductInventory = async (productId: string): Promise<InventoryWithLocation[]> => {
  const response = await axiosInstance.get(`/inventory/product/${productId}/all`);
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const adjustStock = async (productId: string, data: StockAdjustmentFormData) => {
  const response = await axiosInstance.post(`/inventory/product/${productId}/adjust`, data);
  return response.data;
};

export const QuickStockAdjustmentDialog = ({
  open,
  onClose,
  productId,
  preselectedLocationId,
}: QuickStockAdjustmentDialogProps) => {
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm<StockAdjustmentFormData>({
    defaultValues: {
      locationId: preselectedLocationId || '',
      quantityChange: 0,
      reason: 'adjustment',
      notes: '',
    },
  });

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  // Fetch inventory locations for this product
  const { data: inventory = [], isLoading: inventoryLoading } = useQuery({
    queryKey: ['productInventory', productId],
    queryFn: () => fetchProductInventory(productId),
    enabled: open && !!productId,
  });

  // Update form when preselectedLocationId changes
  useEffect(() => {
    if (preselectedLocationId) {
      setValue('locationId', preselectedLocationId);
    }
  }, [preselectedLocationId, setValue]);

  const adjustMutation = useMutation({
    mutationFn: (data: StockAdjustmentFormData) => adjustStock(productId, data),
    onSuccess: () => {
      showMessage('Stock adjusted successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['productInventory', productId] });
      queryClient.invalidateQueries({ queryKey: ['productMovements', productId] });
      reset();
      onClose();
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to adjust stock', 'error');
    },
  });

  const onSubmit = (data: StockAdjustmentFormData) => {
    adjustMutation.mutate(data);
  };

  const handleClose = () => {
    if (!adjustMutation.isPending) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          border: `2px solid ${palette.primary}`,
          borderRadius: theme.radius.md,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: typography.fontFamily.display,
          fontWeight: typography.fontWeight.bold,
          color: colors.textPrimary,
          textTransform: 'uppercase',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <EditIcon sx={{ color: palette.primary }} />
        Quick Stock Adjustment
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box component="form" noValidate>
          {/* Location Selection */}
          <Controller
            name="locationId"
            control={control}
            rules={{ required: 'Location is required' }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={inventory}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    const inv = inventory.find((i) => i.locationId === option);
                    return inv?.locationName || inv?.location?.name || option;
                  }
                  return option.locationName || option.location?.name || option.locationId;
                }}
                value={inventory.find((i) => i.locationId === field.value) || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.locationId || '');
                }}
                loading={inventoryLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    error={!!errors.locationId}
                    helperText={errors.locationId?.message}
                    margin="normal"
                    fullWidth
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {inventoryLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.locationId === value?.locationId}
              />
            )}
          />

          {/* Quantity Change */}
          <TextField
            {...register('quantityChange', {
              valueAsNumber: true,
              required: 'Quantity change is required',
              validate: (value) => value !== 0 || 'Quantity change cannot be zero',
            })}
            error={!!errors.quantityChange}
            helperText={errors.quantityChange?.message || 'Enter positive number to add stock, negative to remove'}
            margin="normal"
            fullWidth
            label="Quantity Change"
            type="number"
            required
          />

          {/* Reason Selection */}
          <FormControl fullWidth margin="normal" error={!!errors.reason} required>
            <InputLabel>Reason</InputLabel>
            <Controller
              name="reason"
              control={control}
              rules={{ required: 'Reason is required' }}
              render={({ field }) => (
                <Select {...field} label="Reason">
                  <MenuItem value="restock">Restock</MenuItem>
                  <MenuItem value="loss">Loss</MenuItem>
                  <MenuItem value="count">Count Adjustment</MenuItem>
                  <MenuItem value="adjustment">Manual Adjustment</MenuItem>
                </Select>
              )}
            />
            {errors.reason && (
              <FormHelperText>{errors.reason.message}</FormHelperText>
            )}
          </FormControl>

          {/* Notes */}
          <TextField
            {...register('notes')}
            error={!!errors.notes}
            helperText={errors.notes?.message || 'Optional notes about this adjustment'}
            margin="normal"
            fullWidth
            label="Notes"
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.surfaceElevated,
          px: 3,
          py: 2,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={adjustMutation.isPending}
          sx={{
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            textTransform: 'uppercase',
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.wide,
            color: colors.textMuted,
            '&:hover': {
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={adjustMutation.isPending}
          startIcon={adjustMutation.isPending ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            textTransform: 'uppercase',
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.wide,
            color: palette.primary,
            border: `1px solid ${palette.primary}`,
            borderRadius: theme.radius.xs,
            '&:hover': {
              backgroundColor: alpha(palette.primary, 0.1),
            },
            '&:disabled': {
              opacity: 0.6,
            },
          }}
        >
          {adjustMutation.isPending ? 'Adjusting...' : 'Adjust Stock'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
